import $ from 'jquery'
import Cookies from 'js-cookie'
import jstz from 'jstz'

import {
  createForm,
  composeValidations,
  validateNoLeadingSpaces,
  validateLengthInRange,
  validateMinLength,
  validateRegexp,
  ValidationFunction,
} from '../lib/form-logic'

import { executeRecaptcha, isRecaptchaEnabled } from './recaptcha'

import {
  MaybeValidationError,
  StringFieldView,
  BooleanFieldView,
  ValidationError,
} from './form-field-view'

import { isValidEmail } from './helpers'

const debug = require('debug')('pm:signup')

const makeNameValidation = (remoteValidateUrl: string) =>
  composeLocalAndRemoteValidations<string>(
    /* prettier-ignore */
    [
      s => validateLengthInRange('Your name must be between 2 and 120 characters long.', 2, 120, s),
      s => validateNoLeadingSpaces("Please don't use leading spaces", s)
    ],
    s => remoteValidate(remoteValidateUrl, { name: s })
  )

const makeEmailValidation = (remoteValidateUrl: string) =>
  composeLocalAndRemoteValidations<string>(
    /* prettier-ignore */
    [
      s => localValidateEmail('Please enter a valid email address. Example: john@xyzwidgets.com', s)
    ],
    s => remoteValidate(remoteValidateUrl, { email: s })
  )

const makeLoginValidation = (remoteValidateUrl: string) =>
  composeLocalAndRemoteValidations<string>(
    /* prettier-ignore */
    [
      s => validateLengthInRange('Your username must be between 3 and 50 characters long.', 3, 50, s),
      s => validateNoLeadingSpaces("Please don't use leading spaces", s),
      s => validateRegexp('Your username may only contain letters, numbers, and some special characters -_@+.', /^[a-zA-Z0-9.+\-_@]+$/, s)
    ],
    s => remoteValidate(remoteValidateUrl, { login: s })
  )

const makePasswordValidation = () =>
  composeLocalAndRemoteValidations<string>(
    /* prettier-ignore */
    [
      s => validateMinLength('Your password must be 6 characters or longer.', 6, s)
    ]
  )

const makeTermsValidation = () =>
  composeLocalAndRemoteValidations<boolean>(
    /* prettier-ignore */
    [
      checked => !checked ? 'Please accept our Terms of Service to create a Postmark account' : undefined
    ]
  )

$(function signupPage() {
  const $form = $('.js-signup-form')

  // Return if we're not on the signup/invitation page
  if ($form.length === 0) return

  setTimezone()
  setMonthlyVolume()

  const $signUpButton = $form.find('button[type=submit]')
  const $nameInput = $form.find('.js-validate-name')
  const $emailInput = $form.find('.js-validate-email')
  const $usernameInput = $form.find('.js-validate-username')
  const $passwordInput = $form.find('.js-validate-password')
  const $termsCheckbox = $form.find('.js-validate-terms')

  const validateName = makeNameValidation($nameInput.data('uri'))
  const validateEmail = makeEmailValidation($emailInput.data('uri'))
  const validateLogin = makeLoginValidation($usernameInput.data('uri'))
  const validatePassword = makePasswordValidation()
  const validateTerms = makeTermsValidation()

  const form = createForm<ValidationError>()
  form.addField(new StringFieldView($nameInput), validateName)
  form.addField(new StringFieldView($emailInput), validateEmail)
  form.addField(new StringFieldView($usernameInput), validateLogin)
  form.addField(new StringFieldView($passwordInput), validatePassword)
  form.addField(new BooleanFieldView($termsCheckbox), validateTerms)

  $form.on('submit.signup', async function validateSignup(event) {
    event.preventDefault()
    debug('submit form')

    disableSubmitButton()

    try {
      const formValid = await form.validate()

      if (!formValid) {
        enableSubmitButton()

        debug('form is not valid. not submitting')

        return
      }

      if (isRecaptchaEnabled()) {
        // we need to enable the button in case the user cancels reCAPTCHA
        enableSubmitButton()

        executeRecaptcha(() => {
          debug('recaptcha ok. submitting')

          disableSubmitButton()
          submitForm()
        })
      } else {
        debug('no recaptcha. submitting')
        submitForm()
      }
    } catch (e) {
      enableSubmitButton()
      // let Bugsnag catch it
      throw e
    }
  })

  function enableSubmitButton() {
    $signUpButton.prop('disabled', false)
  }

  function disableSubmitButton() {
    $signUpButton.prop('disabled', true)
  }

  function submitForm() {
    $form.off('submit.signup').submit()
  }

  function setTimezone() {
    const timezone = jstz.determine().name()

    debug('timezone', timezone)

    $('.js-timezone').val(timezone)
  }

  function setMonthlyVolume() {
    $('.js-monthly-volume').val(Cookies.get('monthly_volume') || '')
  }
})

type RemoteData = { name: string } | { login: string } | { email: string }

function remoteValidate(url: string, data: RemoteData): Promise<string | null> {
  debug(`remoteValidate('${url}') ->`, data)
  return new Promise((resolve, reject) => {
    $.ajax({
      url,
      data,
      method: 'POST',
      dataType: 'json',
    })
      .done(response => {
        debug(`remoteValidate('${url}') <-`, response)

        if (response.isValid) {
          resolve(null)
        } else {
          resolve(response.message)
        }
      })
      .fail((jqXHR, textStatus, errorThrown) => {
        debug(`remoteValidate('${url}') failed`, { textStatus, errorThrown })
        reject(new Error(errorThrown || textStatus))
      })
  })
}

type RemoteValidationFunction<T> = (value: T) => Promise<string | null>

function composeLocalAndRemoteValidations<T>(
  local: ValidationFunction<T>[],
  remote?: RemoteValidationFunction<T>
) {
  const validateLocal = composeValidations(...local)

  return async (value: T): Promise<MaybeValidationError> => {
    const msg = validateLocal(value)
    if (msg) {
      return {
        text: msg,
      }
    }

    if (!remote) {
      return null
    }

    const errorMarkup = await remote(value)
    if (!errorMarkup) return null

    return {
      markup: errorMarkup,
    }
  }
}

function localValidateEmail(msg: string, s: string) {
  if (!isValidEmail(s)) return msg
}
